import { useState, useEffect, createContext, useContext, FC } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import * as _ from "lodash";

import {
  Enterprise,
  EnterpriseAppState,
  FarmEntity,
  FarmList,
  PensList,
  HerdGroupList,
  SiteEntity
} from "@ctra/api";

import { Nullable, Cookies, CookieKeys, parseQuery } from "@ctra/utils";

import { getStorageKey } from "../../../../utils/versioning";
import { useFarmNamePreference } from "./useFarmNamePreference";
import moment from "moment";

interface ContextType {
  farm: Nullable<FarmEntity>;
  site: Nullable<SiteEntity>;
  pens: PensList;
  herdGroups: HerdGroupList;
  meta: {
    lastContextUpdate: moment.Moment;
    useObfuscatedNames: boolean;
  };
  api: {
    setUseObfuscatedNames: (value: boolean | ((prevState: boolean) => boolean)) => void;
    setFarmContext: (farmID: Nullable<FarmEntity["id"]>, siteID?: SiteEntity["id"]) => void;
    getFarmName: (farm: FarmEntity, site?: Nullable<SiteEntity>) => string;
    getSiteName: (site: SiteEntity) => string;
  };
}

/**
 * Farm context session key
 * @type {string}
 */
export const farmSessionKey = `${getStorageKey({ persist: true })}.farmContext`;

/**
 * Site context session key
 * @type {string}
 */
export const siteSessionKey = `${getStorageKey({ persist: true })}.siteContext`;

/**
 * Default farm context
 */
export const DefaultFarmContext = createContext<ContextType>({
  farm: null,
  site: null,
  pens: {},
  herdGroups: {},
  meta: {
    lastContextUpdate: moment(),
    useObfuscatedNames: Cookies.get(CookieKeys.farmNamePreference) === "obfuscated"
  },
  api: {
    setUseObfuscatedNames: _.noop,
    setFarmContext: _.noop,
    getFarmName: (farm) => farm.name || "Unknown Farm",
    getSiteName: (site) => site.name || "Unknown Site"
  }
});

/**
 * Custom farm context provider which sets the farm context based on the selected farm.
 * For persistence, it uses session storage and clears the key when a user logs out/closes the tab.
 * @note Due to the fact that some pages in the app uses farmID as a query param for their business logic,
 * external URLS will make use of farmContext as their query param which will then be deleted once the farm context
 * has been set. This helps us avoid a conflict in query param names.
 * @param {boolean | undefined} local
 * @param {number | undefined} defaultFarmID
 * @param {string | undefined} defaultSiteID
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @returns {JSX.Element}
 * @constructor
 */
const FarmContextProvider: FC<{
  local?: boolean;
  farmID?: FarmEntity["id"];
  siteID?: SiteEntity["id"];
}> = ({ local, farmID: defaultFarmID, siteID: defaultSiteID, children }) => {
  const { pathname, search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const [lastContextUpdate, setLastContextUpdate] = useState<moment.Moment>(moment());

  const { farmContext: farmContextSearchParam, siteContext: siteContextSearchParam } = parseQuery(
    searchParams,
    "farmContext",
    "siteContext"
  );

  const { useObfuscatedNames, setUseObfuscatedNames } = useFarmNamePreference();

  /**
   * Fetch the initial farmID from the url farmContext param or the session storage
   */
  const [farmID, setFarmID] = useState<Nullable<FarmEntity["id"]>>(
    defaultFarmID || farmContextSearchParam || JSON.parse(sessionStorage.getItem(farmSessionKey) as string)
  );

  /**
   * Fetch the initial siteID from the url siteContext param or the session storage
   */
  const [siteID, setSiteID] = useState<Nullable<SiteEntity["id"]>>(
    defaultSiteID || siteContextSearchParam || sessionStorage.getItem(siteSessionKey)
  );

  /**
   * Get the farm list
   */
  const farmList = useSelector<EnterpriseAppState, FarmList>((state) =>
    Enterprise.entities.getFarmList(state)
  );

  /**
   * Get all sites from the store
   * @type {Record<SiteEntity["id"], SiteEntity>}
   */
  const siteList = useSelector<EnterpriseAppState, Record<SiteEntity["id"], SiteEntity>>((state) =>
    Enterprise.entities.getSiteList(state)
  );

  /**
   * Get the pen list from the store
   * @type {PensList}
   */
  const pens = useSelector<EnterpriseAppState, PensList>((state) =>
    farmID ? Enterprise.entities.getPens(state, { farmID, siteID: _.defaultTo(siteID, void 0) }) : {}
  );

  /**
   * Get the herd group list from the store
   * @type {HerdGroupList}
   */
  const herdGroups = useSelector<EnterpriseAppState, HerdGroupList>((state) =>
    farmID ? Enterprise.entities.getHerdGroups(state, { farmID, siteID: _.defaultTo(siteID, void 0) }) : {}
  );

  /**
   * Set farm and site ID when the default values change
   */
  useEffect(() => {
    if (defaultFarmID !== farmID) {
      setFarmID(defaultFarmID);
    }

    if (defaultSiteID !== siteID) {
      setSiteID(defaultSiteID);
    }
    // eslint-disable-next-line
  }, [defaultFarmID, defaultSiteID]);

  /**
   * Set the farm context when the search params change
   */
  useEffect(() => {
    setFarmContext(farmID, siteID);

    searchParams.delete("farmContext");
    searchParams.delete("siteContext");

    Enterprise.history.push({ pathname, search: searchParams.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmContextSearchParam, siteContextSearchParam]);

  /**
   * Remove the selected farm object from session storage when the user logs out
   */
  useEffect(() => {
    return () => {
      if (!local) {
        sessionStorage.removeItem(farmSessionKey);
        sessionStorage.removeItem(siteSessionKey);
      }

      setFarmID(null);
      setSiteID(null);
    };
  }, [local]);

  /**
   * If the user has only one farm, set it in the context
   */
  useEffect(() => {
    const farmIDs = _.keys(farmList) as unknown as Array<FarmEntity["id"]>;

    if (farmIDs.length === 1 && !farmID) {
      setFarmContext(Number(_.first(farmIDs)));
    }
    // eslint-disable-next-line
  }, [farmList]);

  /**
   * Save selected farm object to session storage and set the context value
   * @param {Nullable<FarmEntity["id"]>} farmID
   * @param {Nullable<SiteEntity["id"]>} siteID
   */
  const setFarmContext = (farmID: Nullable<FarmEntity["id"]>, siteID?: Nullable<SiteEntity["id"]>) => {
    /**
     * This ensures that we do not lose farm data on browser page refresh
     */
    if (!local) {
      sessionStorage.setItem(farmSessionKey, JSON.stringify(farmID));
    }

    setFarmID(farmID);

    if (siteID) {
      if (!local) {
        sessionStorage.setItem(siteSessionKey, siteID);
      }

      setSiteID(siteID);
    } else {
      if (!local) {
        sessionStorage.removeItem(siteSessionKey);
      }

      setSiteID(null);
    }

    setLastContextUpdate(moment());
  };

  /**
   * Find the farm in the store
   * @todo check what happens if the farm list is not yet loaded
   */
  const farm = farmID ? farmList[farmID] : null;

  /**
   * Get the selected site if it belongs to the selected farm
   * @type {SiteEntity | null}
   */
  const site = siteID && farm?.sites?.includes(siteID) ? siteList[siteID] : null;

  /**
   * Get farm name based on user preference
   */
  const getFarmName = (farm: FarmEntity, site: Nullable<SiteEntity>): string => {
    if (useObfuscatedNames && farm.obfuscatedName) {
      return farm.obfuscatedName;
    }

    return site ? `${farm.name}: ${getSiteName(site)}` : farm.name || "Unknown Farm";
  };

  /**
   * Get site name or return "Unknown Site" if not available
   * @param {SiteEntity} site
   * @returns {string}
   */
  const getSiteName = (site: SiteEntity): string => {
    return site.name || "Unknown Site";
  };

  return (
    <DefaultFarmContext.Provider
      value={{
        farm,
        site,
        pens,
        herdGroups,
        meta: {
          useObfuscatedNames,
          lastContextUpdate
        },
        api: {
          setUseObfuscatedNames,
          setFarmContext,
          getFarmName,
          getSiteName
        }
      }}
    >
      {children}
    </DefaultFarmContext.Provider>
  );
};

export const FarmContext = {
  Provider: FarmContextProvider,
  Consumer: DefaultFarmContext.Consumer,
  Context: DefaultFarmContext
};

/**
 * Hook for farm context
 * @returns {void}
 */
export const useFarm = (): ContextType => useContext<ContextType>(DefaultFarmContext);
